import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Quote } from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';

/* Chapter — opinionated section block. Big italic-accent title on the
   left rail, body copy on the right, with generous breathing room. */
const Chapter = ({ eyebrow, title, italic, children, dataTestId }) => (
  <section
    className="px-6 py-16 md:py-24"
    data-testid={dataTestId}
  >
    <div className="max-w-5xl mx-auto grid md:grid-cols-12 gap-x-10 gap-y-8">
      <div className="md:col-span-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4">
          {eyebrow}
        </p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a]">
          {title}{' '}
          {italic && <em className="italic font-light text-blue-600">{italic}</em>}
        </h2>
      </div>
      <div className="md:col-span-7 md:col-start-6 space-y-5 text-[17px] md:text-lg leading-relaxed text-[#0b1f3a]/80">
        {children}
      </div>
    </div>
  </section>
);

const LevantaStory = () => {
  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* HERO — same sky-gradient palette as the homepage so the transparent
          header has a colored backdrop to sit on at rest. */}
      <section className="relative pt-[110px] md:pt-[170px] pb-14 md:pb-32 px-6 overflow-hidden min-h-[580px] md:min-h-[760px]">
        <AltParallaxBackdrop />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7"
            data-testid="story-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 inline-block" />
            How it all started
          </span>
          <h1
            className="text-5xl md:text-7xl lg:text-[88px] font-black leading-[1.02] tracking-tight text-[#0b1f3a] alt-hero-glow"
          >
            Levanta&rsquo;s
            <br />
            <em className="italic font-light text-blue-600">story.</em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            How a personal practice became a platform — communities, podcasts, books,
            courses, and the realization that pulled them all into one system.
          </p>
        </div>
      </section>

      {/* OPENING — single bold line that grabs the reader. */}
      <section className="px-6 py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-3xl md:text-5xl font-black leading-[1.15] tracking-tight text-[#0b1f3a]">
            Levanta did not begin as a platform.{' '}
            <em className="italic font-light text-blue-600">It began as a personal search.</em>
          </p>
          <p className="mt-8 text-base md:text-lg text-[#0b1f3a]/65 leading-relaxed">
            For the deeply personal side of that search — the years Lev spent rebuilding
            himself before any of this existed —{' '}
            <Link
              to="/founder"
              className="text-blue-600 font-semibold hover:text-blue-700 underline decoration-blue-600/30 underline-offset-4 hover:decoration-blue-600 transition-colors"
            >
              read About the Founder
            </Link>
            . This page is about something different: how that private journey became a
            public system.
          </p>
        </div>
      </section>

      {/* Pull-quote — the question that turned a personal practice outward. */}
      <section className="px-6 py-16 md:py-20 bg-[#f4f8fc]">
        <div className="max-w-4xl mx-auto relative">
          <Quote
            className="absolute -top-6 -left-2 md:-top-14 md:-left-10 w-12 h-12 md:w-28 md:h-28 text-blue-300/15 -scale-x-100"
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <p className="relative text-2xl md:text-4xl font-semibold leading-snug text-[#0b1f3a] italic">
            &ldquo;If this path transformed me, how can it help others?&rdquo;
          </p>
          <p className="relative mt-5 text-sm uppercase tracking-[0.2em] text-blue-600 font-semibold">
            The question that built a platform
          </p>
        </div>
      </section>

      {/* Chapter 1 — the experiments */}
      <Chapter
        eyebrow="Chapter 01"
        title="So he started"
        italic="experimenting."
        dataTestId="story-chapter-1"
      >
        <p>Some attempts gained traction. Some faded. Some evolved into something new.</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 not-prose">
          {[
            'Built small communities',
            'Launched an Instagram page',
            'Started a podcast',
            'Wrote two books',
            'Spoke on stages',
            'Created online courses',
          ].map((item) => (
            <li
              key={item}
              className="flex items-start gap-3 px-4 py-3 rounded-xl bg-white ring-1 ring-black/5"
            >
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
              <span className="text-[15px] md:text-base text-[#0b1f3a]/80">{item}</span>
            </li>
          ))}
        </ul>
        <p>
          At the time they felt like separate attempts — different ideas, different
          directions.
        </p>
        <p className="font-semibold text-[#0b1f3a]">But each one taught something important.</p>
      </Chapter>

      {/* Chapter 3 — what was learned */}
      <Chapter
        eyebrow="Chapter 02"
        title="The"
        italic="lessons."
        dataTestId="story-chapter-2"
      >
        <div className="grid grid-cols-1 gap-3">
          {[
            ['How people engage', 'And how quickly they disengage.'],
            ['Inspiration attracts attention', "It doesn't guarantee consistency."],
            ['Communities need guidance', 'Not just motivation.'],
            ['Information alone', "Doesn't create lasting change."],
          ].map(([head, tail]) => (
            <div
              key={head}
              className="p-5 rounded-2xl bg-[#fafbfd] ring-1 ring-black/5"
            >
              <p className="text-[#0b1f3a] font-semibold">{head}</p>
              <p className="mt-1 text-[#0b1f3a]/65 text-[15px]">{tail}</p>
            </div>
          ))}
        </div>
      </Chapter>

      {/* Pattern reveal — the three-line manifesto. */}
      <section className="px-6 py-20 md:py-28 bg-[#0b1f3a] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-6">
            Over the years, patterns became clear.
          </p>
          <div className="space-y-4 text-3xl md:text-5xl font-black leading-tight tracking-tight">
            <p>
              Growth without structure{' '}
              <em className="italic font-light text-blue-300">collapses.</em>
            </p>
            <p>
              Energy without direction{' '}
              <em className="italic font-light text-blue-300">fades.</em>
            </p>
            <p>
              Ideas without systems{' '}
              <em className="italic font-light text-blue-300">disappear.</em>
            </p>
          </div>
        </div>
      </section>

      {/* Chapter 4 — the realization. */}
      <Chapter
        eyebrow="Chapter 03"
        title="Levanta"
        italic="emerged."
        dataTestId="story-chapter-3"
      >
        <p>
          It became the integration of everything that came before — the communication
          knowledge, the community building, the content creation, the educational
          experiments, the business attempts, and the personal transformation behind
          it all.
        </p>
        <p className="font-semibold text-[#0b1f3a]">
          Instead of isolated projects, Levanta became a unified system.
        </p>
        <div className="space-y-2 pl-4 border-l-2 border-blue-400">
          <p>Not just content.</p>
          <p>Not just inspiration.</p>
          <p className="font-semibold text-[#0b1f3a]">
            A designed environment for long-term development.
          </p>
        </div>
        <p>
          Looking back now, the earlier projects were not wasted effort. They were
          preparation. Each one refined the vision. Each one removed what didn&rsquo;t
          work. Each one clarified what was missing.
        </p>
      </Chapter>

      {/* Closing — punctuation. */}
      <section className="px-6 py-20 md:py-28 bg-gradient-to-b from-white to-[#f4f8fc]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-2xl md:text-3xl text-[#0b1f3a]/85 leading-relaxed">
            Levanta is the result of that process —{' '}
            <span className="font-semibold text-[#0b1f3a]">
              years of learning, testing, building, failing, adjusting and rebuilding.
            </span>
          </p>
          <p className="mt-10 text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600">
            And it continues to evolve the same way it was created
          </p>
          <p className="mt-4 text-4xl md:text-7xl font-black leading-tight tracking-tight text-[#0b1f3a]">
            Step <em className="italic font-light text-blue-600">by</em> step.
          </p>
        </div>
      </section>

      {/* CTA band — same dark navy CTA as homepage / FAQ. */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-black leading-tight">
            See where the{' '}
            <em className="italic font-light text-blue-300">story is going.</em>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-white/75">
            The system is live. The next chapter is yours.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/download-app"
              data-testid="story-cta-download"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#0b1f3a] font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Download Levanta
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/vision"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white/5 text-white font-semibold ring-1 ring-white/20 hover:bg-white/10 transition-all duration-200"
            >
              Read our vision
            </Link>
          </div>
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default LevantaStory;
